import { ScrollReveal } from "@/components/ScrollReveal";
import { AccordionFAQ } from "@/components/AccordionFAQ";
import { SEO } from "@/components/SEO";
import { CONTACT, ROUTE_PATHS } from "@/lib/index";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const faqItems = [
  {
    question: "À qui s'adresse cette formation B2B ?",
    answer: "À tout professionnel coach, formateur, entrepreneur qui cherche à vendre des offres high-ticket à des professionnels ou des particuliers. La formation est construite sur la réalité terrain 2026."
  },
  {
    question: "Comment fonctionne l'option appels de closing à 10% ?",
    answer: "Pendant l'apprentissage, si tu as besoin de support sur tes appels de closing, je prends la main à ta place — en m'adaptant à ton offre spécifique. Si la vente se conclut, je perçois 10% du montant. Tu gardes 90% et tu observes la structure d'un appel adapté à ton marché."
  },
  {
    question: "Pourquoi zéro roleplay dans la formation ?",
    answer: "Le roleplay donne une illusion de préparation mais ne reproduit pas la complexité des vraies conversations B2B. Ici, chaque exercice se déroule sur de vrais prospects, avec l'IA pour t'aider à analyser les profils, calibrer tes messages et identifier les angles d'approche."
  },
  {
    question: "Quelle différence entre les deux formules ?",
    answer: "La formule Full Autonome donne accès à tout le contenu pour avancer à ton rythme. La formule One-to-One y ajoute un accompagnement personnalisé : visioconférences régulières, suivi de ton CRM et de tes KPIs, feedback sur tes conversations et travail spécifique sur ta posture et ton autorité commerciale."
  },
  {
    question: "En combien de temps je serai opérationnel ?",
    answer: "Pour une maîtrise complète et des compétences pleinement monétisables, compte 6 mois de pratique intensive. Tu peux commencer à générer des résultats concrets dès la phase de setting (mois 3-4), selon la qualité de ton engagement dans les exercices terrain."
  },
  {
    question: "La formation couvre aussi l'aspect administratif ?",
    answer: "Oui. La capsule bonus couvre intégralement la structuration administrative de ton activité : statut légal, contrats, facturation, organisation. Tout ce qu'il faut pour exercer de façon professionnelle et sécurisée."
  }
];

export default function FormationB2B() {
  return (
    <>
      <SEO 
        title="Formation B2B Setting & Closing — Shut Up & Close"
        description="Formation complète en Setting et Closing B2B. Vends des offres high-ticket sans forcer. Méthode terrain pour professionnels, coachs et entrepreneurs."
        canonical="https://camillevega.fr/#/formation-b2b"
        type="course"
      />
      <div className="min-h-screen" style={{ backgroundColor: '#1E130C', color: '#EDE8DF' }}>
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md" style={{ backgroundColor: 'rgba(30, 19, 12, 0.85)' }}>
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="text-2xl font-bold transition-opacity hover:opacity-80" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#D4580E' }}>
            Shut Up & Close
          </Link>
          <div className="hidden md:flex items-center gap-8" style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem' }}>
            <Link to={ROUTE_PATHS.HOME} className="hover:opacity-70 transition-opacity">
              Accueil
            </Link>
            <a href="#programme" onClick={(e) => { e.preventDefault(); document.getElementById('programme')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:opacity-70 transition-opacity cursor-pointer">
              Programme
            </a>
            <a href="#offres" onClick={(e) => { e.preventDefault(); document.getElementById('offres')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:opacity-70 transition-opacity cursor-pointer">
              Offres
            </a>
          </div>
          <a
            href="https://wa.me/33695771167?text=Bonjour%20Camille%2C%20j%27ai%20d%C3%A9couvert%20ton%20site%20et%20je%20voudrais%20qu%27on%20%C3%A9change%20de%20vive%20voix.%20Quelles%20sont%20tes%20disponibilit%C3%A9s%20pour%20un%20appel%20%3F"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-2 rounded-sm font-medium transition-all hover:scale-105"
            style={{ backgroundColor: '#D4580E', color: '#1E130C', fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Demander un appel
          </a>
        </div>
      </nav>

      <section className="min-h-screen pt-32 pb-20 px-8">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest mb-6" style={{ fontFamily: 'Space Mono, monospace', color: '#9A8878' }}>
              — Formation B2B · Setting & Closing · 2026
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', lineHeight: '1.1' }}>
              Se vendre sans jamais <span style={{ fontStyle: 'italic', color: '#D4580E' }}>forcer</span> la <span style={{ textDecoration: 'underline', textDecorationColor: '#D4580E', textDecorationThickness: '2px' }}>décision</span>.
            </h1>
            <p className="text-lg mb-8 leading-relaxed" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
              Le commercial B2B de 2026 n'est plus un vendeur. C'est un diagnosticien stratégique qui révèle les blocages, qualifie chirurgicalement et guide naturellement vers une décision éclairée. Shut Up & Close t'apprend exactement ça.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/33695771167?text=Bonjour%20Camille%2C%20j%27ai%20d%C3%A9couvert%20ton%20site%20et%20je%20voudrais%20qu%27on%20%C3%A9change%20de%20vive%20voix.%20Quelles%20sont%20tes%20disponibilit%C3%A9s%20pour%20un%20appel%20%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-sm font-semibold transition-all hover:scale-105"
                style={{ backgroundColor: '#D4580E', color: '#1E130C', fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Demander un appel
              </a>
              <a
                href="https://wa.me/33695771167?text=Bonjour%20Camille%2C%20j%27ai%20d%C3%A9couvert%20ton%20site%20et%20j%27aimerais%20en%20savoir%20plus%20sur%20ta%20formation.%20Peux-tu%20m%27envoyer%20plus%20d%27informations%20%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-sm font-medium transition-all hover:bg-opacity-10"
                style={{ border: '1px solid #EDE8DF', color: '#EDE8DF', fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Poser une question
              </a>
            </div>
          </div>

          <div className="p-8 rounded-sm" style={{ backgroundColor: '#2A1A10', border: '1px solid rgba(212, 88, 14, 0.3)' }}>
            <div className="text-sm uppercase tracking-widest mb-6" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
              // Résultats du nouveau paradigme
            </div>
            <div className="space-y-6">
              {[
                { label: 'Taux de présence aux appels', value: 90, display: '90%+' },
                { label: 'Maîtrise opérationnelle', value: 100, display: '6 mois' },
                { label: 'Roleplay dans la formation', value: 0, display: '0' },
                { label: 'Commission sur vos ventes', value: 10, display: '10%' }
              ].map((metric, idx) => (
                <div key={idx}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
                      {metric.label}
                    </span>
                    <span className="text-lg font-bold" style={{ fontFamily: 'Space Mono, monospace', color: '#E8651A' }}>
                      {metric.display}
                    </span>
                  </div>
                  <div className="h-1 rounded-full" style={{ backgroundColor: '#321F14' }}>
                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${metric.value}%`, backgroundColor: '#D4580E' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-8" style={{ backgroundColor: '#321F14' }}>
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="text-xs uppercase tracking-widest mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
              Le diagnostic
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Ton cycle de vente est long. Tes prospects procrastinent. Ton closing stagne.
            </h2>
            <p className="text-lg mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
              Ce n'est pas un problème de compétence. C'est un problème de paradigme. Les méthodes qui fonctionnaient il y a 5 ans génèrent aujourd'hui du rejet. Voici pourquoi.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Le prospect est sur-sollicité',
                description: "Bombardé de messages, de pitchs et de propositions, le décideur B2B a développé une immunité quasi-totale aux approches commerciales classiques. Il les détecte en quelques secondes."
              },
              {
                title: 'Tu vends avant qu\'il soit prêt',
                description: "Le cerveau humain traverse des étapes mentales précises avant toute décision. Proposer trop tôt — avant la qualification, avant la résonance — génère du rejet et du ghosting systématique."
              },
              {
                title: 'Ton CRM ne travaille pas pour toi',
                description: "70% des revenus potentiels s'évaporent faute de suivi structuré. Sans architecture de données, les prospects qualifiés disparaissent silencieusement dans le vide."
              }
            ].map((card, idx) => (
              <ScrollReveal key={idx} delay={idx * 0.1}>
                <div className="p-8 rounded-sm h-full transition-all hover:scale-[1.02]" style={{ backgroundColor: '#2A1A10' }}>
                  <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {card.title}
                  </h3>
                  <p style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                    {card.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <ScrollReveal>
              <div className="text-xs uppercase tracking-widest mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
                Le changement de paradigme
              </div>
              <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                L'ancien modèle est mort.
              </h2>
              <p className="mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.7' }}>
                Le setting transactionnel des années 2010-2020 est caduc. Le prospect moderne ne veut pas être convaincu. Il veut être compris, éclairé et guidé vers sa propre décision.
              </p>
              <p style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.7' }}>
                Shut Up & Close t'apprend la posture du diagnosticien : celui qui révèle l'invisible, qualifie chirurgicalement et crée les conditions où la décision devient évidente — sans forcing.
              </p>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.2}>
            <div className="p-8 rounded-sm" style={{ backgroundColor: '#2A1A10' }}>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-xs uppercase tracking-widest mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#9A8878' }}>
                    Ancienne approche
                  </div>
                  <div className="space-y-3" style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.9rem', color: '#9A8878' }}>
                    <div style={{ textDecoration: 'line-through', opacity: 0.6 }}>Pousser vers la décision</div>
                    <div style={{ textDecoration: 'line-through', opacity: 0.6 }}>Convaincre à tout prix</div>
                    <div style={{ textDecoration: 'line-through', opacity: 0.6 }}>Surmonter les objections</div>
                    <div style={{ textDecoration: 'line-through', opacity: 0.6 }}>Créer l'urgence artificielle</div>
                    <div style={{ textDecoration: 'line-through', opacity: 0.6 }}>Volume d'appels brut</div>
                    <div style={{ textDecoration: 'line-through', opacity: 0.6 }}>Scripts standardisés</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
                    Nouveau paradigme
                  </div>
                  <div className="space-y-3" style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.9rem', color: '#EDE8DF' }}>
                    <div>Clarifier la situation</div>
                    <div>Révéler les enjeux réels</div>
                    <div>Comprendre les blocages</div>
                    <div>Sécuriser la réflexion</div>
                    <div>Qualification chirurgicale</div>
                    <div>Posture diagnosticienne</div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section id="programme" className="py-20 px-8" style={{ backgroundColor: '#321F14' }}>
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="text-xs uppercase tracking-widest mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
              Le programme
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Deux capsules. De la prospection au closing.
            </h2>
            <p className="text-lg mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
              Chaque capsule est composée de contenus structurés : vidéo explicative, PDF de référence, fiches outils opérationnels et exercices d'application terrain. Zéro roleplay. Tu t'entraînes sur de vrais prospects, calibré par l'IA.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <ScrollReveal delay={0.1}>
              <div className="p-8 rounded-sm" style={{ backgroundColor: '#2A1A10' }}>
                <div className="text-xs uppercase tracking-widest mb-2" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
                  // CAPSULE 01
                </div>
                <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Setting
                </h3>
                <p className="mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                  Du premier message au rendez-vous qualifié. Construire ta crédibilité, détecter la saturation, structurer la conversation, booker avec un taux de présence de 90%+.
                </p>
                <div className="text-sm font-bold mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#E8651A' }}>
                  13 capsules
                </div>
                <p className="text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                  La capsule couvre quatre grands axes : la construction d'une posture et d'un positionnement crédibles en environnement B2B, la maîtrise de la conversation commerciale de la prise de contact jusqu'au booking qualifié, l'architecture d'un système CRM orienté performance, et l'intégration de l'IA comme outil d'analyse et de calibration au quotidien.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="p-8 rounded-sm" style={{ backgroundColor: '#2A1A10' }}>
                <div className="text-xs uppercase tracking-widest mb-2" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
                  // CAPSULE 02
                </div>
                <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Closing
                </h3>
                <p className="mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                  De l'appel qualifié à la décision signée. Structurer chaque appel, traiter les objections profondes, lire les profils psychologiques, et conclure sans jamais forcer.
                </p>
                <div className="text-sm font-bold mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#E8651A' }}>
                  13 capsules
                </div>
                <p className="text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                  La capsule couvre trois grands axes : la lecture des profils décisionnels et de la psychologie d'achat B2B, la structuration d'un appel de closing de l'ouverture à la signature, et la gestion de la relation post-appel pour maintenir un pipeline actif et qualifié.
                </p>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.3}>
            <div className="p-8 rounded-sm" style={{ backgroundColor: '#2A1A10' }}>
              <div className="text-xs uppercase tracking-widest mb-2" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
                // CAPSULE BONUS
              </div>
              <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Administratif & Structuration
              </h3>
              <p style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                Contrats, facturation, statut légal, organisation de l'activité. Tout ce qu'il faut pour exercer de façon professionnelle et sécurisée — la partie que personne n'enseigne.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.4}>
            <div className="mt-12">
              <div className="text-xs uppercase tracking-widest mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
                Format pédagogique
              </div>
              <h3 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Du contenu dense. Des outils immédiatement utilisables.
              </h3>
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  { title: '1 vidéo par module', desc: 'Explications claires, denses, sans remplissage. Un module = un concept maîtrisé.' },
                  { title: 'PDF de référence', desc: 'Un support structuré pour chaque capsule. À consulter, annoter, réutiliser.' },
                  { title: 'Fiches outils', desc: 'Des frameworks opérationnels à intégrer directement dans vos conversations B2B.' },
                  { title: 'Exercices terrain', desc: 'Missions sur vrais prospects, assistées par l\'IA. Zéro roleplay, 100% réalité.' }
                ].map((item, idx) => (
                  <div key={idx}>
                    <h4 className="font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {item.title}
                    </h4>
                    <p className="text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <ScrollReveal>
              <div className="text-xs uppercase tracking-widest mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
                Exclusivité B2B
              </div>
              <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Besoin de support sur tes appels ? Je les prends.
              </h2>
              <p className="mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.7' }}>
                Pendant ton apprentissage, si tu as besoin que je prenne en charge tes appels de vente, je m'adapte à ton offre et je gère les calls à ta place. Si je signe — je prends une commission.
              </p>
              <div className="p-6 rounded-sm" style={{ backgroundColor: '#2A1A10', border: '1px solid #D4580E' }}>
                <div className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#E8651A' }}>
                  10% de commission
                </div>
                <p className="text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
                  Je prends en charge tes appels de closing pendant la montée en compétences. Tu gardes 90% de la vente et tu observes comment un appel se structure dans ton univers B2B spécifique.
                </p>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.2}>
            <div>
              <div className="text-xs uppercase tracking-widest mb-6" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
                Progression en 6 mois
              </div>
              <div className="space-y-6">
                {[
                  { period: 'Mois 1–2', title: 'Posture & fondations', desc: 'Paramétrage du CRM, définition de la niche, construction du profil et des premiers messages.' },
                  { period: 'Mois 3–4', title: 'Setting & qualification', desc: 'Ouverture de conversations, diagnostic de saturation, booking qualifié. Premiers revenus possibles.' },
                  { period: 'Mois 5–6', title: 'Closing & autonomie', desc: 'Maîtrise complète des appels, traitement des objections, closing autonome. Compétences pleinement monétisables.' }
                ].map((phase, idx) => (
                  <div key={idx} className="p-6 rounded-sm" style={{ backgroundColor: '#2A1A10' }}>
                    <div className="text-xs uppercase tracking-widest mb-2" style={{ fontFamily: 'Space Mono, monospace', color: '#E8651A' }}>
                      {phase.period}
                    </div>
                    <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {phase.title}
                    </h4>
                    <p className="text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                      {phase.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-12 px-8" style={{ backgroundColor: '#321F14' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '0', label: 'Roleplay — terrain réel uniquement' },
              { value: '6 mois', label: 'Pour monétiser complètement' },
              { value: 'IA', label: 'Intégrée dans chaque exercice terrain' },
              { value: '10%', label: 'Commission uniquement si je close' }
            ].map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 0.1}>
                <div>
                  <div className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Mono, monospace', color: '#E8651A' }}>
                    {item.value}
                  </div>
                  <div className="text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
                    {item.label}
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="offres" className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="text-xs uppercase tracking-widest mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
              Ton investissement
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Deux voies. Un seul objectif.
            </h2>
            <p className="text-lg mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
              Selon ton niveau d'autonomie et ton besoin de cadrage, choisis la formule qui te correspond.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal delay={0.1}>
              <div className="p-8 rounded-sm" style={{ backgroundColor: '#2A1A10' }}>
                <div className="text-xs uppercase tracking-widest mb-2" style={{ fontFamily: 'Space Mono, monospace', color: '#9A8878' }}>
                  Formule — E-Learning
                </div>
                <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Full Autonome
                </h3>
                <div className="text-4xl font-bold mb-6" style={{ fontFamily: 'Space Mono, monospace', color: '#E8651A' }}>
                  €1 500 <span className="text-lg" style={{ color: '#9A8878' }}>TTC</span>
                </div>
                <p className="mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                  Accès complet à toutes les capsules. Tu avances à ton rythme avec l'intégralité du contenu, des outils et de l'IA intégrée.
                </p>
                <div className="space-y-3 mb-8">
                  {[
                    'Capsules Setting + Closing complètes',
                    'Capsule bonus administrative',
                    'Vidéos, PDFs, fiches outils & exercices terrain',
                    'Outil IA d\'analyse de profils prospects',
                    'Option appels de closing (10% si je close)',
                    'Mises à jour incluses'
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <span style={{ color: '#D4580E' }}>✦</span>
                      <span className="text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
                <a
                  href="https://wa.me/33695771167?text=Bonjour%20Camille%2C%20j%27ai%20d%C3%A9couvert%20ton%20site%20et%20je%20voudrais%20qu%27on%20%C3%A9change%20de%20vive%20voix.%20Quelles%20sont%20tes%20disponibilit%C3%A9s%20pour%20un%20appel%20%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-8 py-4 rounded-sm font-semibold text-center transition-all hover:scale-105"
                  style={{ backgroundColor: '#D4580E', color: '#1E130C', fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  Demander un appel
                </a>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="p-8 rounded-sm relative" style={{ backgroundColor: '#2A1A10', border: '2px solid #D4580E' }}>
                <div className="absolute -top-3 left-8 px-4 py-1 rounded-sm text-xs font-bold" style={{ backgroundColor: '#D4580E', color: '#1E130C', fontFamily: 'Space Mono, monospace' }}>
                  Recommandé
                </div>
                <div className="text-xs uppercase tracking-widest mb-2" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
                  Formule — Accompagnement
                </div>
                <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  One-to-One
                </h3>
                <div className="text-4xl font-bold mb-6" style={{ fontFamily: 'Space Mono, monospace', color: '#E8651A' }}>
                  €2 400 <span className="text-lg" style={{ color: '#9A8878' }}>TTC</span>
                </div>
                <p className="mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878', lineHeight: '1.6' }}>
                  L'intégralité de la formation e-learning, enrichie d'un accompagnement personnalisé pour aller plus vite et construire une posture commerciale solide.
                </p>
                <div className="space-y-3 mb-8">
                  {[
                    'Tout ce qui est inclus dans Full Autonome',
                    'Visioconférences one-to-one régulières',
                    'Suivi de ton CRM & analyse de tes KPIs',
                    'Travail sur ta posture & ton autorité commerciale',
                    'Feedback personnalisé sur tes conversations',
                    'Option appels de closing (10% si je close)'
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <span style={{ color: '#D4580E' }}>✦</span>
                      <span className="text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
                <a
                  href="https://wa.me/33695771167?text=Bonjour%20Camille%2C%20j%27ai%20d%C3%A9couvert%20ton%20site%20et%20je%20voudrais%20qu%27on%20%C3%A9change%20de%20vive%20voix.%20Quelles%20sont%20tes%20disponibilit%C3%A9s%20pour%20un%20appel%20%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-8 py-4 rounded-sm font-semibold text-center transition-all hover:scale-105"
                  style={{ backgroundColor: '#D4580E', color: '#1E130C', fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  Demander un appel
                </a>
              </div>
            </ScrollReveal>
          </div>

          <div className="text-center mt-8 text-sm" style={{ fontFamily: 'Space Mono, monospace', color: '#9A8878' }}>
            Maîtrise complète & compétences monétisables · Estimation : 6 mois
          </div>
        </div>
      </section>

      <section className="py-20 px-8" style={{ backgroundColor: '#321F14' }}>
        <div className="max-w-4xl mx-auto">
          <ScrollReveal>
            <div className="text-xs uppercase tracking-widest mb-4" style={{ fontFamily: 'Space Mono, monospace', color: '#D4580E' }}>
              Questions
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Réponses directes à vos vraies questions.
            </h2>
          </ScrollReveal>
          <AccordionFAQ items={faqItems} />
        </div>
      </section>

      <section className="py-24 px-8 text-center">
        <div className="max-w-4xl mx-auto">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Le seul moyen de vendre mieux, c'est de comprendre comment il décide.
            </h2>
            <p className="text-lg mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#9A8878' }}>
              Rejoins Shut Up & Close. Une formation B2B construite sur la réalité du terrain — pas sur des scripts que tes prospects reconnaissent de loin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/33695771167?text=Bonjour%20Camille%2C%20j%27ai%20d%C3%A9couvert%20ton%20site%20et%20je%20voudrais%20qu%27on%20%C3%A9change%20de%20vive%20voix.%20Quelles%20sont%20tes%20disponibilit%C3%A9s%20pour%20un%20appel%20%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-sm font-semibold transition-all hover:scale-105"
                style={{ backgroundColor: '#D4580E', color: '#1E130C', fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Demander un appel
              </a>
              <a
                href="https://wa.me/33695771167?text=Bonjour%20Camille%2C%20j%27ai%20d%C3%A9couvert%20ton%20site%20et%20j%27aimerais%20en%20savoir%20plus%20sur%20ta%20formation.%20Peux-tu%20m%27envoyer%20plus%20d%27informations%20%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-sm font-medium transition-all hover:bg-opacity-10"
                style={{ border: '1px solid #EDE8DF', color: '#EDE8DF', fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Poser une question
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <footer className="py-8 px-8" style={{ backgroundColor: '#0F0A07', borderTop: '1px solid rgba(212, 88, 14, 0.2)' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#D4580E' }}>
            Shut Up & Close
          </div>
          <div className="text-sm" style={{ fontFamily: 'Space Mono, monospace', color: '#9A8878' }}>
            Formation B2B · Setting & Closing · 2026
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}